import { useCallback, useState } from "react";
import { Building2, Globe2, Users } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../connect/api";
import { useConnect } from "../../connect/store";
import { officeLabel } from "./DeptGroup";
import { Avatar, Empty, Header, LoadMore, usePaged } from "./ui";

/** Members of a department or office group — managers first, then assistant managers, then everyone else. */
export const GroupMembers = () => {
  const { me } = useConnect();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const department = params.get("department") || "";
  const office = params.get("office") || "";
  const [q, setQ] = useState("");
  const fetcher = useCallback((p) => api.get("/groups/members", { params: { department, office, q, page: p, limit: 40 } }).then((r) => r.data), [department, office, q]);
  const list = usePaged(fetcher, [department, office, q]);
  const title = office || department;
  const rankLabel = (s, i) => {
    const prev = list.items[i - 1];
    if (i > 0 && prev.rank === s.rank) return null;
    return { 0: "Managers", 1: "Assistant Managers", 2: "Team" }[s.rank ?? 2];
  };
  return (
    <div className="h-full flex flex-col">
      <Header title={office ? officeLabel(office) : department} sub={`${list.total ?? ""} members · managers first`} back={`/connect/group?tab=${office ? "office" : "dept"}`} right={<span className="w-9 h-9 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">{office ? <Globe2 size={16} className="text-blue-400" /> : <Building2 size={16} className="text-blue-400" />}</span>} />
      <div className="px-3 py-2 shrink-0"><input className="nxc-input py-2.5" placeholder={`Search in ${title}`} value={q} onChange={(e) => setQ(e.target.value)} data-testid="group-members-search" /></div>
      <div className="flex-1 overflow-y-auto px-3 pb-4" onScroll={list.onScroll} data-testid="group-members-list">
        {!list.loading && list.items.length === 0 && <Empty icon={Users} title="No members found" />}
        <ul className="space-y-1.5">
          {list.items.map((s, i) => {
            const label = rankLabel(s, i);
            return (
              <li key={s.id}>
                {label && <div className="text-[11px] uppercase tracking-wider text-amber-300/80 px-1 pt-2 pb-1">{label}</div>}
                <div className={`nxc-card p-3 flex items-center gap-3 ${me.is_ceo ? "nxc-card-hover cursor-pointer" : ""}`} onClick={() => me.is_ceo && nav(`/connect/staff/${s.id}`)} data-testid={`group-member-${s.id}`}>
                  <Avatar name={s.name} src={s.avatar} ceo={s.is_ceo} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-100 truncate">{s.name}{s.id === me.id && <span className="text-[10px] text-blue-300 ml-1.5">you</span>}</div>
                    <div className="text-xs text-slate-400 truncate">{s.title}</div>
                    <div className="text-[11px] text-slate-500 truncate">{office ? s.department : officeLabel(s.office)}</div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
        <LoadMore hasMore={list.hasMore} loading={list.loading && list.items.length > 0} onClick={list.loadMore} />
      </div>
    </div>
  );
};
